import {
  formatCountdown,
  parseCountdownDate,
  startCountdown,
} from "@lyle/countdown";

class LyleHeroCarousel extends HTMLElement {
  #stopCountdowns = [];
  #listeners = [];

  connectedCallback() {
    this.#initCarousel();
    this.#initVideos();
    this.#initCountdowns();
  }

  disconnectedCallback() {
    for (const stop of this.#stopCountdowns) {
      stop();
    }
    this.#stopCountdowns = [];

    for (const { target, type, handler } of this.#listeners) {
      target.removeEventListener(type, handler);
    }
    this.#listeners = [];
  }

  #initCarousel() {
    const slideCount = Number.parseInt(this.dataset.slideCount || "0", 10);
    const slides = [...this.querySelectorAll("[data-hero-carousel-slide]")];
    const arrows = this.querySelector("[data-hero-carousel-arrows]");
    const prevButton = this.querySelector("[data-hero-carousel-prev]");
    const nextButton = this.querySelector("[data-hero-carousel-next]");
    let activeIndex = slides.findIndex((slide) =>
      slide.classList.contains("is-active"),
    );

    if (activeIndex < 0) activeIndex = 0;

    this.classList.add("is-animated");

    const showSlide = (index) => {
      const nextIndex = (index + slides.length) % slides.length;
      slides[activeIndex]?.classList.remove("is-active");
      slides[nextIndex]?.classList.add("is-active");
      activeIndex = nextIndex;
    };

    if (slideCount <= 1) {
      arrows?.classList.add("is-hidden");
      return;
    }

    this.#addListener(prevButton, "click", () => showSlide(activeIndex - 1));
    this.#addListener(nextButton, "click", () => showSlide(activeIndex + 1));
    arrows?.classList.remove("is-hidden");
  }

  #addListener(target, type, handler) {
    if (!target) return;
    target.addEventListener(type, handler);
    this.#listeners.push({ target, type, handler });
  }

  #initVideos() {
    if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const videos = this.querySelectorAll(
      ".hero-carousel__video-mobile, .hero-carousel__video-desktop",
    );
    for (const video of videos) {
      video.removeAttribute("autoplay");
    }
  }

  #initCountdowns() {
    const countdowns = this.querySelectorAll("[data-hero-carousel-countdown]");

    for (const countdown of countdowns) {
      this.#bindCountdown(countdown);
    }
  }

  #bindCountdown(countdown) {
    const panel = countdown.closest("[data-hero-carousel-countdown-panel]");
    if (!panel) return;

    const endDate = parseCountdownDate(countdown.dataset.countdown);
    if (!endDate) return;

    const duringCta = panel.querySelector(
      "[data-hero-carousel-countdown-cta-during]",
    );
    const endedCta = panel.querySelector(
      "[data-hero-carousel-countdown-cta-ended]",
    );

    const stop = startCountdown(
      endDate,
      (timeLeft) => {
        countdown.textContent = formatCountdown(timeLeft);
      },
      () => {
        duringCta?.classList.add("is-hidden");
        endedCta?.classList.remove("is-hidden");
        countdown.remove();
      },
    );

    this.#stopCountdowns.push(stop);
  }
}

if (!customElements.get("lyle-hero-carousel")) {
  customElements.define("lyle-hero-carousel", LyleHeroCarousel);
}
